import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { useFieldArray, useForm, useWatch, type Resolver } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useNavigate } from "@tanstack/react-router";
import { format, parseISO } from "date-fns";
import { ArrowLeft, Copy, History, Loader2, Plus, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  CollapsibleBlock,
  DocumentHeader,
  DocumentPage,
  DocumentStickyBar,
  useDocumentSaveShortcut,
} from "@/components/shared/document-shell";
import { EntityPicker } from "@/components/shared/entity-picker";
import { ErrorBoundary } from "@/components/shared/error-boundary";
import { Field, FieldGrid, FormSection } from "@/components/shared/form";
import {
  LineGrid,
  LineGridBody,
  LineGridHead,
  LineGridRow,
  LineGridTd,
  LineGridTh,
  blockNumberWheel,
  cellControlClass,
} from "@/components/shared/line-grid";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input, NativeSelect } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { formatMoney } from "@/features/clients/constants";
import {
  buildOrderSpecDescription,
  calcWtPerMm,
  materialFieldRules,
} from "@/features/purchase/material-fields";
import { qtyFromTotalWeight, totalWeightFromQty, numOrNull } from "@/features/purchase/tube-weight";
import { TUBE_ORDER_SHAPES, formatShapeSizeLabel } from "@/features/materials/constants";
import {
  computeLineAmounts,
  computePoTotals,
  purchaseOrderToForm,
  useCreatePurchaseOrder,
  usePurchaseOrder,
  usePurchaseOrders,
  useUpdatePurchaseOrder,
} from "@/features/purchase/api";
import { canEditLines, labelFor, PO_STATUSES } from "@/features/purchase/constants";
import {
  emptyPoLine,
  emptyPurchaseOrderForm,
  purchaseOrderFormSchema,
  type PurchaseOrderFormValues,
} from "@/features/purchase/schema";
import type { PurchaseOrder } from "@/features/purchase/types";
import {
  clearPoFormDraft,
  isMeaningfulPoDraft,
  loadPoFormDraft,
  poFormDraftKey,
  savePoFormDraft,
} from "@/features/purchase/po-form-draft";
import { useCompany, useTaxRates } from "@/features/settings/api";
import { GST_SLABS, listGstSlabs, resolveApplicableTaxes } from "@/features/settings/constants";
import { useVendors } from "@/features/vendors/api";
import { PAYMENT_TERMS } from "@/features/vendors/constants";
import { cn } from "@/lib/utils";
import {
  useMaterialGrades,
  useMaterials,
  useShapeSizes,
} from "@/features/materials/api";

/** Apply a prior PO as a new draft template (fresh dates, new line ids). */
function formFromPriorPo(po: PurchaseOrder): PurchaseOrderFormValues {
  const base = purchaseOrderToForm(po);
  const today = new Date().toISOString().slice(0, 10);
  return {
    ...base,
    issue_date: today,
    due_date: "",
    lines:
      base.lines.length > 0
        ? base.lines.map(({ id: _id, ...line }) => line)
        : [emptyPoLine()],
  };
}

export function PurchaseOrderNewPage() {
  return (
    <ErrorBoundary title="PO form crashed">
      <PurchaseOrderFormPage mode="create" />
    </ErrorBoundary>
  );
}

export function PurchaseOrderEditPage({ poId }: { poId: string }) {
  return (
    <ErrorBoundary title="PO form crashed">
      <PurchaseOrderFormPage mode="edit" poId={poId} />
    </ErrorBoundary>
  );
}

function PurchaseOrderFormPage({ mode, poId }: { mode: "create" | "edit"; poId?: string }) {
  const navigate = useNavigate();
  const isEdit = mode === "edit";
  const draftKey = poFormDraftKey(isEdit ? "edit" : "create", poId);
  const { data: po, isLoading } = usePurchaseOrder(isEdit ? poId : undefined);
  const { data: vendors = [] } = useVendors();
  const { data: materials = [] } = useMaterials();
  const { data: grades = [] } = useMaterialGrades();
  const { data: company } = useCompany();
  const { data: taxRates = [] } = useTaxRates();
  const createPo = useCreatePurchaseOrder();
  const updatePo = useUpdatePurchaseOrder();
  const [historyOpen, setHistoryOpen] = useState(false);
  const [applyingId, setApplyingId] = useState<string | null>(null);
  const hydratedRef = useRef(false);
  const restoreToastRef = useRef(false);

  const initialDraft = !isEdit ? loadPoFormDraft(draftKey) : null;

  const form = useForm<PurchaseOrderFormValues>({
    resolver: zodResolver(purchaseOrderFormSchema) as unknown as Resolver<PurchaseOrderFormValues>,
    defaultValues:
      initialDraft && isMeaningfulPoDraft(initialDraft) ? initialDraft : emptyPurchaseOrderForm,
    mode: "onBlur",
  });

  const { fields, append, remove } = useFieldArray({ control: form.control, name: "lines" });
  const values = useWatch({ control: form.control });
  const linesEditable = !isEdit || (po ? canEditLines(po.status) : true);
  const vendorId = values.vendor_id;
  const { data: vendorHistory = [], isFetching: historyLoading } = usePurchaseOrders(
    vendorId ? { vendorId } : undefined,
  );
  const { data: sourcePo, isFetching: sourceLoading } = usePurchaseOrder(applyingId ?? undefined);

  // Edit: hydrate from draft (unsaved) or server once
  useEffect(() => {
    if (!isEdit || !po || hydratedRef.current) return;
    const draft = loadPoFormDraft(draftKey);
    if (draft && isMeaningfulPoDraft(draft)) {
      form.reset(draft);
      if (!restoreToastRef.current) {
        restoreToastRef.current = true;
        toast.message("Restored unsaved edits");
      }
    } else {
      form.reset(purchaseOrderToForm(po));
    }
    hydratedRef.current = true;
  }, [isEdit, po, draftKey, form]);

  // Create: one-time restore toast if draft was used as defaultValues
  useEffect(() => {
    if (isEdit || restoreToastRef.current) return;
    if (initialDraft && isMeaningfulPoDraft(initialDraft)) {
      restoreToastRef.current = true;
      toast.message("Restored unsaved draft");
    }
  }, [isEdit, initialDraft]);

  // Persist while typing / navigating away (session tab)
  useEffect(() => {
    const sub = form.watch((vals) => {
      savePoFormDraft(draftKey, vals as PurchaseOrderFormValues);
    });
    return () => sub.unsubscribe();
  }, [form, draftKey]);

  useEffect(() => {
    if (!isEdit && company && !form.getValues("bill_to_text")) {
      const bill = [company.name, company.addr1, [company.city, company.pincode].filter(Boolean).join(" — ")]
        .filter(Boolean)
        .join("\n");
      form.setValue("bill_to_text", bill);
    }
  }, [isEdit, company, form]);

  useEffect(() => {
    if (!applyingId || !sourcePo || sourcePo.id !== applyingId) return;
    form.reset(formFromPriorPo(sourcePo));
    toast.success(`Copied lines & terms from ${sourcePo.po_number}`);
    setApplyingId(null);
    setHistoryOpen(false);
  }, [applyingId, sourcePo, form]);

  const vendor = vendors.find((v) => v.id === vendorId);
  const priorOrders = useMemo(
    () => vendorHistory.filter((row) => row.id !== poId && row.status !== "cancelled"),
    [vendorHistory, poId],
  );
  const lastPrior = priorOrders[0];
  const canCopyHistory = Boolean(vendorId) && linesEditable;
  const slabs = useMemo(() => {
    const fromRates = listGstSlabs(taxRates);
    return fromRates.length ? fromRates : [...GST_SLABS];
  }, [taxRates]);

  const preview = useMemo(() => {
    const lines = values.lines ?? [];
    const computed = lines.map((line) =>
      computeLineAmounts(
        {
          qty: Number(line.qty) || 0,
          weight: Number(line.weight) || 0,
          unit_rate: Number(line.unit_rate) || 0,
          gst_slab: Number(line.gst_slab) || 0,
        },
        taxRates,
        company?.state,
        vendor?.place_of_supply || vendor?.state,
        values.issue_date || new Date().toISOString().slice(0, 10),
      ),
    );
    return {
      lines: computed,
      totals: computePoTotals(computed, Number(values.discount) || 0, Number(values.adjustment) || 0),
      mode: resolveApplicableTaxes({
        rates: taxRates,
        gstSlab: 18,
        companyState: company?.state,
        clientPlaceOfSupply: vendor?.place_of_supply || vendor?.state,
      }).mode,
    };
  }, [values, taxRates, company?.state, vendor]);

  const vendorOptions = useMemo(
    () =>
      vendors
        .filter((v) => v.is_active || v.id === vendorId)
        .map((v) => ({
          id: v.id,
          label: `${v.code} · ${v.name}`,
          keywords: `${v.code} ${v.name} ${v.gstin ?? ""} ${v.city ?? ""}`,
          meta: v.city || undefined,
        })),
    [vendors, vendorId],
  );

  const materialOptions = useMemo(() => {
    const lineIds = new Set((values.lines ?? []).map((l) => l.material_id).filter(Boolean));
    return materials
      .filter((m) => m.is_active || lineIds.has(m.id))
      .map((m) => ({
        id: m.id,
        label: m.name,
        keywords: `${m.name} ${m.batch_prefix ?? ""} ${m.category ?? ""}`,
        meta: m.batch_prefix || undefined,
      }));
  }, [materials, values.lines]);

  const saving = createPo.isPending || updatePo.isPending;
  const errors = form.formState.errors;

  const submitIntentRef = useRef<"save" | "send">("save");
  const canSend = !isEdit || po?.status === "draft";

  const submit = form.handleSubmit(async (formValues) => {
    for (let i = 0; i < formValues.lines.length; i++) {
      const line = formValues.lines[i];
      const m = materials.find((x) => x.id === line.material_id);
      if (!m) continue;
      const rules = materialFieldRules({
        material_id: m.id,
        material_name: m.name,
        requires_shape_size: m.requires_shape_size,
        parent_material_id: m.parent_material_id,
      });
      if (rules.requireShapeSize && !line.shape_size_id) {
        toast.error(`Line ${i + 1}: Shape and Size are required for ${m.name}`);
        return;
      }
    }
    const send = submitIntentRef.current === "send";
    if (isEdit && poId) {
      await updatePo.mutateAsync({
        id: poId,
        values: formValues,
        replaceLines: linesEditable,
        send: send && po?.status === "draft",
      });
      clearPoFormDraft(draftKey);
      void navigate({ to: "/purchase/orders/$poId", params: { poId } });
      return;
    }
    const created = await createPo.mutateAsync({ values: formValues, send });
    clearPoFormDraft(draftKey);
    void navigate({ to: "/purchase/orders/$poId", params: { poId: created.id } });
  });

  const save = useCallback(() => {
    submitIntentRef.current = "save";
    void submit();
  }, [submit]);

  const saveAndSend = useCallback(() => {
    submitIntentRef.current = "send";
    void submit();
  }, [submit]);

  useDocumentSaveShortcut(save, !saving);

  if (isEdit && isLoading) {
    return <p className="py-16 text-center text-sm text-muted-foreground">Loading PO…</p>;
  }

  if (isEdit && po && po.status === "cancelled") {
    return (
      <div className="space-y-3 py-8 text-center">
        <p className="text-sm text-muted-foreground">This PO cannot be edited.</p>
        <Button asChild variant="outline" size="sm">
          <Link to="/purchase/orders/$poId" params={{ poId: po.id }}>
            Back to view
          </Link>
        </Button>
      </div>
    );
  }

  return (
    <DocumentPage>
      <DocumentHeader
        back={
          <Button asChild variant="ghost" size="icon-sm">
            {isEdit && poId ? (
              <Link to="/purchase/orders/$poId" params={{ poId }}>
                <ArrowLeft />
              </Link>
            ) : (
              <Link to="/purchase">
                <ArrowLeft />
              </Link>
            )}
          </Button>
        }
        title={isEdit ? `Edit ${po?.po_number ?? "PO"}` : "New purchase order"}
        subtitle={
          <>
            GST: {preview.mode === "intra_state" ? "CGST+SGST" : "IGST"}
            {!linesEditable ? " · Lines locked" : ""}
            {" · Ctrl+S save"}
          </>
        }
        actions={
          <>
            {canCopyHistory ? (
              <>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={!lastPrior || Boolean(applyingId)}
                  title={lastPrior ? `Copy from ${lastPrior.po_number}` : "No prior PO for this vendor"}
                  onClick={() => {
                    if (!lastPrior) {
                      toast.message("No prior purchase order for this vendor");
                      return;
                    }
                    setApplyingId(lastPrior.id);
                  }}
                >
                  {applyingId && applyingId === lastPrior?.id && sourceLoading ? (
                    <Loader2 className="animate-spin" />
                  ) : (
                    <Copy />
                  )}
                  Last PO
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setHistoryOpen(true)}
                >
                  <History />
                  From history
                </Button>
              </>
            ) : null}
            <Button asChild type="button" variant="outline" size="sm">
              <Link to="/purchase">Cancel</Link>
            </Button>
            <Button type="button" size="sm" variant="outline" disabled={saving} onClick={save}>
              {saving ? <Loader2 className="animate-spin" /> : null}
              Save
            </Button>
            {canSend ? (
              <Button type="button" size="sm" disabled={saving} onClick={saveAndSend}>
                {saving ? <Loader2 className="animate-spin" /> : <Send />}
                Save & send
              </Button>
            ) : null}
          </>
        }
      />

      <form
        className="space-y-2"
        onSubmit={(e) => {
          e.preventDefault();
          save();
        }}
      >
        <FormSection title="Header" variant="plain">
          <FieldGrid>
            <Field label="Vendor" required span={4} error={errors.vendor_id?.message}>
              <EntityPicker
                value={values.vendor_id ?? ""}
                disabled={!linesEditable && isEdit}
                placeholder="Type vendor code or name…"
                recentKey="vendors"
                options={vendorOptions}
                onChange={(id) => {
                  form.setValue("vendor_id", id, { shouldDirty: true, shouldValidate: true });
                  const v = vendors.find((x) => x.id === id);
                  if (v?.payment_terms) {
                    form.setValue("payment_terms", v.payment_terms, { shouldDirty: true });
                  }
                }}
              />
            </Field>
            <Field label="Issue" required span={2} error={errors.issue_date?.message}>
              <Input type="date" {...form.register("issue_date")} />
            </Field>
            <Field label="Due" span={2}>
              <Input type="date" {...form.register("due_date")} />
            </Field>
            <Field label="Payment" span={4}>
              <NativeSelect {...form.register("payment_terms")}>
                <option value="">—</option>
                {PAYMENT_TERMS.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </NativeSelect>
            </Field>
          </FieldGrid>

          <CollapsibleBlock title="Bill to / Ship to" className="mt-1">
            <FieldGrid>
              <Field label="Bill to" span={6}>
                <Textarea rows={2} className="min-h-0 text-xs" {...form.register("bill_to_text")} />
              </Field>
              <Field label="Ship to" span={6}>
                <Textarea rows={2} className="min-h-0 text-xs" {...form.register("ship_to_text")} />
              </Field>
            </FieldGrid>
          </CollapsibleBlock>
        </FormSection>

        <FormSection
          title="Lines"
          variant="plain"
          aside={
            linesEditable ? (
              <Button
                type="button"
                size="xs"
                variant="outline"
                onClick={() => append(emptyPoLine())}
              >
                <Plus />
                Add
              </Button>
            ) : null
          }
        >
          {errors.lines?.message || errors.lines?.root?.message ? (
            <p className="mb-1 text-2xs text-destructive">
              {errors.lines?.message || errors.lines?.root?.message}
            </p>
          ) : null}
          <p className="mb-1.5 text-2xs text-muted-foreground">
            Coil → Thk / Width / Wt · Tube/CTL → Shape + Size (required) + Len; weight auto from
            formula, rate always on weight. Add sizes under Materials → Shapes.
          </p>

          <LineGrid minWidth="70rem">
            <LineGridHead>
              <LineGridTh className="w-7 text-center">#</LineGridTh>
              <LineGridTh className="min-w-[8rem]">Material</LineGridTh>
              <LineGridTh className="min-w-[4.5rem]">Grade</LineGridTh>
              <LineGridTh className="w-14" align="right">
                Thk
              </LineGridTh>
              <LineGridTh className="w-16" align="right">
                Width
              </LineGridTh>
              <LineGridTh className="w-16" align="right">
                Len
              </LineGridTh>
              <LineGridTh className="min-w-[5rem]">Shape</LineGridTh>
              <LineGridTh className="min-w-[6rem]">Size</LineGridTh>
              <LineGridTh className="w-14" align="right">
                Qty
              </LineGridTh>
              <LineGridTh className="w-14" align="right">
                Wt
              </LineGridTh>
              <LineGridTh className="w-[4.5rem]" align="right">
                Rate
              </LineGridTh>
              <LineGridTh className="w-[4.75rem]">GST</LineGridTh>
              <LineGridTh className="min-w-[7rem]">Spec</LineGridTh>
              <LineGridTh className="w-20" align="right">
                Amt
              </LineGridTh>
              <LineGridTh className="w-7" />
            </LineGridHead>
            <LineGridBody>
              {fields.map((field, index) => (
                <PoLineRow
                  key={field.id}
                  index={index}
                  form={form}
                  materials={materials}
                  materialOptions={materialOptions}
                  grades={grades}
                  slabs={slabs}
                  gstMode={preview.mode}
                  lineAmount={preview.lines[index]?.line_subtotal ?? 0}
                  taxHint={preview.lines[index]?.taxes ?? []}
                  linesEditable={linesEditable}
                  canRemove={fields.length > 1 && linesEditable}
                  onRemove={() => remove(index)}
                  onInsertBelow={
                    linesEditable
                      ? () => append(emptyPoLine(), { shouldFocus: false })
                      : undefined
                  }
                />
              ))}
            </LineGridBody>
          </LineGrid>
        </FormSection>

        <CollapsibleBlock title="Notes & terms">
          <FieldGrid>
            <Field label="Notes" span={6}>
              <Textarea rows={2} className="text-xs" {...form.register("notes")} />
            </Field>
            <Field label="Terms" span={6}>
              <Textarea rows={2} className="text-xs" {...form.register("terms")} />
            </Field>
          </FieldGrid>
        </CollapsibleBlock>

        <CollapsibleBlock title="Discount & adjustment">
          <FieldGrid>
            <Field label="Discount" span={3}>
              <Input
                type="number"
                step="0.01"
                className="fs-no-spin"
                onWheel={blockNumberWheel}
                {...form.register("discount")}
              />
            </Field>
            <Field label="Adjustment" span={3} hint="Can be negative">
              <Input
                type="number"
                step="0.01"
                className="fs-no-spin"
                onWheel={blockNumberWheel}
                {...form.register("adjustment")}
              />
            </Field>
          </FieldGrid>
        </CollapsibleBlock>
      </form>

      <DocumentStickyBar>
        <div className="flex flex-wrap items-center gap-3 text-xs">
          <span>
            Sub{" "}
            <strong className="tabular-nums">{formatMoney(preview.totals.subtotal)}</strong>
          </span>
          <span>
            Tax{" "}
            <strong className="tabular-nums">{formatMoney(preview.totals.tax_total)}</strong>
          </span>
          <span>
            Grand{" "}
            <strong className="tabular-nums text-primary">
              {formatMoney(preview.totals.grand_total)}
            </strong>
          </span>
          <span className="text-2xs text-muted-foreground">{fields.length} lines</span>
        </div>
        <div className="flex items-center gap-1.5">
          {canCopyHistory ? (
            <>
              <Button
                type="button"
                size="xs"
                variant="outline"
                disabled={!lastPrior || Boolean(applyingId)}
                onClick={() => {
                  if (!lastPrior) {
                    toast.message("No prior purchase order for this vendor");
                    return;
                  }
                  setApplyingId(lastPrior.id);
                }}
              >
                <Copy />
                Last PO
              </Button>
              <Button type="button" size="xs" variant="outline" onClick={() => setHistoryOpen(true)}>
                <History />
                History
              </Button>
            </>
          ) : null}
          {linesEditable ? (
            <Button type="button" size="xs" variant="outline" onClick={() => append(emptyPoLine())}>
              <Plus />
              Line
            </Button>
          ) : null}
          <Button type="button" size="sm" variant="outline" disabled={saving} onClick={save}>
            {saving ? <Loader2 className="animate-spin" /> : null}
            Save PO
          </Button>
          {canSend ? (
            <Button type="button" size="sm" disabled={saving} onClick={saveAndSend}>
              {saving ? <Loader2 className="animate-spin" /> : <Send />}
              Save & send
            </Button>
          ) : null}
        </div>
      </DocumentStickyBar>

      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-lg gap-3">
          <DialogHeader>
            <DialogTitle>Copy from vendor history</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">
            Pick a past PO for {vendor ? `${vendor.code} · ${vendor.name}` : "this vendor"}. Lines,
            rates, payment terms, and notes will fill the form. Issue date resets to today.
          </p>
          <div className="max-h-72 overflow-y-auto rounded-md border border-border">
            {historyLoading ? (
              <p className="px-3 py-6 text-center text-xs text-muted-foreground">Loading…</p>
            ) : priorOrders.length === 0 ? (
              <p className="px-3 py-6 text-center text-xs text-muted-foreground">
                No prior purchase orders for this vendor.
              </p>
            ) : (
              <ul className="divide-y divide-border">
                {priorOrders.map((row) => {
                  const issue =
                    row.issue_date &&
                    (() => {
                      try {
                        return format(parseISO(row.issue_date), "dd MMM yyyy");
                      } catch {
                        return row.issue_date;
                      }
                    })();
                  const busy = applyingId === row.id && sourceLoading;
                  return (
                    <li key={row.id}>
                      <button
                        type="button"
                        disabled={Boolean(applyingId)}
                        className="flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-muted/50 disabled:opacity-60"
                        onClick={() => setApplyingId(row.id)}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-medium">{row.po_number}</div>
                          <div className="text-2xs text-muted-foreground">
                            {issue ?? "—"} · {labelFor(PO_STATUSES, row.status)} · {row.line_count}{" "}
                            lines · {formatMoney(row.grand_total)}
                          </div>
                        </div>
                        {busy ? <Loader2 className="size-4 shrink-0 animate-spin" /> : <Copy className="size-3.5 shrink-0 text-muted-foreground" />}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" size="sm" onClick={() => setHistoryOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DocumentPage>
  );
}

function PoLineRow({
  index,
  form,
  materials,
  materialOptions,
  grades,
  slabs,
  gstMode,
  lineAmount,
  taxHint,
  linesEditable,
  canRemove,
  onRemove,
  onInsertBelow,
}: {
  index: number;
  form: ReturnType<typeof useForm<PurchaseOrderFormValues>>;
  materials: {
    id: string;
    name: string;
    uom_id: string;
    is_active: boolean;
    requires_shape_size: boolean;
    parent_material_id?: string | null;
  }[];
  materialOptions: { id: string; label: string; keywords?: string; meta?: string }[];
  grades: { id: string; name: string; is_active: boolean }[];
  slabs: number[];
  gstMode: "intra_state" | "inter_state";
  lineAmount: number;
  taxHint: { tax_type: string; rate: number; amount: number }[];
  linesEditable: boolean;
  canRemove: boolean;
  onRemove: () => void;
  onInsertBelow?: () => void;
}) {
  const materialId = form.watch(`lines.${index}.material_id`);
  const thickness = form.watch(`lines.${index}.thickness`);
  const width = form.watch(`lines.${index}.width`);
  const od = form.watch(`lines.${index}.od`);
  const length = form.watch(`lines.${index}.length`);
  const weight = form.watch(`lines.${index}.weight`);
  const qty = form.watch(`lines.${index}.qty`);
  const shapeSizeId = form.watch(`lines.${index}.shape_size_id`);
  const [shapeKind, setShapeKind] = useState("");
  const lastEditRef = useRef<"qty" | "weight">("qty");

  const material = materials.find((m) => m.id === materialId);
  const rules = material
    ? materialFieldRules({
        material_id: material.id,
        material_name: material.name,
        requires_shape_size: material.requires_shape_size,
        parent_material_id: material.parent_material_id,
      })
    : null;

  const { data: allSizes = [] } = useShapeSizes(rules?.shapeMaterialId || materialId || undefined);
  const lineErrors = form.formState.errors.lines?.[index];
  const taxLabel = taxHint.map((t) => t.tax_type.toUpperCase()).join("+") || "—";
  const wtPerMm =
    rules?.showWtPerMm && Number(weight) > 0 && Number(width) > 0
      ? calcWtPerMm(Number(weight), Number(width))
      : null;

  const selectedSize = useMemo(
    () => allSizes.find((s) => s.id === shapeSizeId),
    [allSizes, shapeSizeId],
  );
  const sizesForShape = useMemo(
    () => allSizes.filter((s) => s.is_active && (!shapeKind || s.shape === shapeKind)),
    [allSizes, shapeKind],
  );

  useEffect(() => {
    if (!shapeSizeId || shapeKind) return;
    if (selectedSize) setShapeKind(selectedSize.shape);
  }, [shapeSizeId, selectedSize, shapeKind]);

  /** Resolve OD / thk / len from watched fields + selected catalogue size */
  function buildTubeDims(
    qtyOverride?: number,
    sizeOverride?: (typeof allSizes)[number] | null,
  ): {
    shape: string;
    od: number | null;
    width: number | null;
    thickness: number | null;
    length: number | null;
    qty: number;
  } {
    const size = sizeOverride ?? selectedSize;
    const shape = (size?.shape || shapeKind || "round").toLowerCase();

    let odN = numOrNull(size?.od) ?? numOrNull(od);
    if (odN == null && size?.size_label) {
      const m = String(size.size_label).match(/(\d+(?:\.\d+)?)/);
      if (m) {
        const parsed = Number(m[1]);
        if (parsed > 0) odN = parsed;
      }
    }

    // Prefer live form values (just-typed thk/len may not be in watch yet)
    const liveThk = numOrNull(form.getValues(`lines.${index}.thickness`));
    const liveLen = numOrNull(form.getValues(`lines.${index}.length`));
    const liveWidth = numOrNull(form.getValues(`lines.${index}.width`));
    const liveQty = Math.max(0, Math.round(Number(form.getValues(`lines.${index}.qty`)) || 0));

    const lineThk = liveThk ?? numOrNull(thickness);
    const catThk = numOrNull(size?.thickness);
    const thk =
      lineThk != null && lineThk > 0
        ? lineThk
        : catThk != null && catThk > 0
          ? catThk
          : null;

    const lineWidth = liveWidth ?? numOrNull(width);
    const catWidth = numOrNull(size?.width);
    const widthN =
      lineWidth != null && lineWidth > 0
        ? lineWidth
        : catWidth != null && catWidth > 0
          ? catWidth
          : null;

    return {
      shape,
      od: odN,
      width: widthN,
      thickness: thk,
      length: liveLen ?? numOrNull(length),
      qty: qtyOverride ?? liveQty,
    };
  }

  /**
   * Bidirectional tube/CTL calc:
   * - prefer "qty"  → weight from qty × formula
   * - prefer "weight" → qty from weight ÷ piece weight
   * If the preferred side is empty/zero, fall back the other way.
   */
  function syncTubeWeight(
    prefer: "qty" | "weight" = lastEditRef.current,
    sizeOverride?: (typeof allSizes)[number] | null,
  ) {
    if (!rules?.autoTubeWeight || !linesEditable) return;
    const dims = buildTubeDims(undefined, sizeOverride);
    const currentQty = dims.qty;
    const currentWt = Number(form.getValues(`lines.${index}.weight`)) || 0;

    if (prefer === "weight" && currentWt > 0) {
      const q = qtyFromTotalWeight(dims, currentWt);
      if (q != null) {
        form.setValue(`lines.${index}.qty`, q, { shouldDirty: true });
      }
      return;
    }

    if (currentQty > 0) {
      const w = totalWeightFromQty(dims);
      if (w != null) {
        form.setValue(`lines.${index}.weight`, w, { shouldDirty: true });
      }
      return;
    }

    if (currentWt > 0) {
      const q = qtyFromTotalWeight(dims, currentWt);
      if (q != null) {
        form.setValue(`lines.${index}.qty`, q, { shouldDirty: true });
      }
    }
  }

  useEffect(() => {
    if (!rules?.autoTubeWeight || !linesEditable) return;
    syncTubeWeight(lastEditRef.current);
    // Do NOT depend on `weight` — writing weight would re-trigger this effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- driven by dim / qty watches
  }, [
    rules?.autoTubeWeight,
    linesEditable,
    shapeSizeId,
    shapeKind,
    length,
    thickness,
    qty,
    od,
    selectedSize?.id,
    selectedSize?.od,
    selectedSize?.width,
    selectedSize?.shape,
    allSizes.length,
  ]);

  useEffect(() => {
    if (!rules || !linesEditable) return;
    const dims = buildTubeDims();
    const spec = buildOrderSpecDescription({
      kind: rules.kind,
      thickness: dims.thickness,
      width: dims.width,
      od: dims.od,
      length: dims.length,
      weight: Number(weight) || 0,
      qty: Number(qty) || 0,
      shapeLabel: selectedSize?.size_label,
    });
    if (spec) {
      form.setValue(`lines.${index}.description`, spec, { shouldDirty: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    rules?.kind,
    thickness,
    width,
    od,
    length,
    weight,
    qty,
    selectedSize?.size_label,
    selectedSize?.od,
    selectedSize?.width,
    selectedSize?.shape,
    shapeKind,
    linesEditable,
    form,
    index,
  ]);

  function onRowKeyDown(e: KeyboardEvent) {
    if (!linesEditable) return;
    if (e.key === "Insert" && onInsertBelow) {
      e.preventDefault();
      onInsertBelow();
    }
  }

  function dimCell(
    name: "thickness" | "width" | "length",
    enabled: boolean,
    hint?: string,
  ) {
    if (!enabled) {
      return (
        <LineGridTd>
          <div className={cellControlClass("bg-muted/30 text-center text-muted-foreground")}>—</div>
        </LineGridTd>
      );
    }
    const raw = form.watch(`lines.${index}.${name}`);
    return (
      <LineGridTd>
        <input
          type="number"
          step="any"
          inputMode="decimal"
          placeholder={hint}
          className={cellControlClass("fs-no-spin text-right tabular-nums")}
          disabled={!linesEditable}
          onWheel={blockNumberWheel}
          onKeyDown={onRowKeyDown}
          value={raw ?? ""}
          onChange={(e) => {
            const v = e.target.value;
            // Dims change → keep qty, recompute weight (unless user last typed weight)
            if (lastEditRef.current !== "weight") lastEditRef.current = "qty";
            form.setValue(`lines.${index}.${name}`, v === "" ? null : Number(v), {
              shouldDirty: true,
            });
            if (rules?.autoTubeWeight) {
              queueMicrotask(() => syncTubeWeight(lastEditRef.current));
            }
          }}
        />
      </LineGridTd>
    );
  }

  return (
    <LineGridRow>
      <LineGridTd className="w-7 text-center tabular-nums text-muted-foreground">
        {index + 1}
      </LineGridTd>
      <LineGridTd>
        <EntityPicker
          dense
          value={materialId ?? ""}
          disabled={!linesEditable}
          placeholder="Coil / Tube…"
          recentKey="materials"
          className={cn(lineErrors?.material_id && "[&_button]:border-destructive")}
          options={materialOptions}
          onChange={(id) => {
            form.setValue(`lines.${index}.material_id`, id, {
              shouldDirty: true,
              shouldValidate: true,
            });
            const m = materials.find((x) => x.id === id);
            setShapeKind("");
            if (m) {
              form.setValue(`lines.${index}.uom_id`, m.uom_id, { shouldDirty: true });
              form.setValue(`lines.${index}.shape_size_id`, "", { shouldDirty: true });
              form.setValue(`lines.${index}.thickness`, null, { shouldDirty: true });
              form.setValue(`lines.${index}.width`, null, { shouldDirty: true });
              form.setValue(`lines.${index}.od`, null, { shouldDirty: true });
              form.setValue(`lines.${index}.length`, null, { shouldDirty: true });
              form.setValue(`lines.${index}.description`, "", { shouldDirty: true });
            }
          }}
        />
      </LineGridTd>
      <LineGridTd>
        <select
          className={cellControlClass()}
          {...form.register(`lines.${index}.material_grade_id`)}
          disabled={!linesEditable}
          onKeyDown={onRowKeyDown}
        >
          <option value="">—</option>
          {grades
            .filter((g) => g.is_active)
            .map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
        </select>
      </LineGridTd>

      {dimCell("thickness", Boolean(rules?.showThickness), rules?.hints.thickness)}
      {dimCell("width", Boolean(rules?.showWidth), rules?.hints.width)}
      {dimCell("length", Boolean(rules?.showLength), rules?.hints.length)}

      <LineGridTd>
        {rules?.showShapeSize ? (
          <select
            className={cn(cellControlClass(), rules.requireShapeSize && !shapeKind && "border-destructive")}
            disabled={!linesEditable}
            value={shapeKind}
            onKeyDown={onRowKeyDown}
            onChange={(e) => {
              setShapeKind(e.target.value);
              form.setValue(`lines.${index}.shape_size_id`, "", { shouldDirty: true });
              form.setValue(`lines.${index}.od`, null, { shouldDirty: true });
              // Keep wall thickness — it is order-specific, not from the size catalogue
            }}
          >
            <option value="">Shape…</option>
            {TUBE_ORDER_SHAPES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        ) : (
          <div className={cellControlClass("bg-muted/30 text-center text-muted-foreground")}>—</div>
        )}
      </LineGridTd>

      <LineGridTd>
        {rules?.showShapeSize ? (
          <select
            className={cn(
              cellControlClass(),
              rules.requireShapeSize && !shapeSizeId && "border-destructive",
            )}
            disabled={!linesEditable || !shapeKind}
            value={shapeSizeId ?? ""}
            onKeyDown={onRowKeyDown}
            onChange={(e) => {
              const id = e.target.value;
              lastEditRef.current = "qty";
              form.setValue(`lines.${index}.shape_size_id`, id, { shouldDirty: true });
              const size = allSizes.find((s) => s.id === id);
              if (size) {
                const sizeOd = numOrNull(size.od);
                if (sizeOd != null) {
                  form.setValue(`lines.${index}.od`, sizeOd, { shouldDirty: true });
                } else if (size.shape === "round" && size.size_label) {
                  const m = String(size.size_label).match(/(\d+(?:\.\d+)?)/);
                  const parsed = m ? Number(m[1]) : NaN;
                  if (Number.isFinite(parsed) && parsed > 0) {
                    form.setValue(`lines.${index}.od`, parsed, { shouldDirty: true });
                  }
                }
                if (size.width != null) {
                  form.setValue(`lines.${index}.width`, numOrNull(size.width), {
                    shouldDirty: true,
                  });
                }
                queueMicrotask(() => syncTubeWeight("qty", size));
              }
            }}
          >
            <option value="">
              {shapeKind
                ? sizesForShape.length
                  ? "Size…"
                  : "No sizes — add in Materials → Shapes"
                : "Pick shape first"}
            </option>
            {sizesForShape.map((s) => (
              <option key={s.id} value={s.id}>
                {formatShapeSizeLabel(s)}
              </option>
            ))}
          </select>
        ) : (
          <div className={cellControlClass("bg-muted/30 text-center text-muted-foreground")}>—</div>
        )}
      </LineGridTd>

      <LineGridTd>
        <input
          type="number"
          step={1}
          min={0}
          inputMode="numeric"
          placeholder={rules?.hints.qty}
          className={cn(
            cellControlClass("fs-no-spin text-right tabular-nums"),
            lineErrors?.qty && "border-destructive",
          )}
          value={qty ?? ""}
          disabled={!linesEditable}
          onWheel={blockNumberWheel}
          onKeyDown={onRowKeyDown}
          onChange={(e) => {
            lastEditRef.current = "qty";
            const raw = e.target.value;
            const v = raw === "" ? 0 : Math.max(0, Math.round(Number(raw)));
            form.setValue(`lines.${index}.qty`, v, { shouldDirty: true });
            if (rules?.autoTubeWeight && v > 0) {
              const w = totalWeightFromQty(buildTubeDims(v));
              if (w != null) {
                form.setValue(`lines.${index}.weight`, w, { shouldDirty: true });
              }
            }
          }}
        />
      </LineGridTd>
      <LineGridTd>
        <input
          type="number"
          step="0.001"
          inputMode="decimal"
          placeholder={rules?.autoTubeWeight ? "auto" : rules?.hints.weight}
          className={cn(
            cellControlClass("fs-no-spin text-right tabular-nums"),
            lineErrors?.weight && "border-destructive",
          )}
          value={weight ?? ""}
          disabled={!linesEditable}
          onWheel={blockNumberWheel}
          onKeyDown={onRowKeyDown}
          title={
            wtPerMm != null
              ? `Wt/mm ${wtPerMm}`
              : rules?.autoTubeWeight
                ? "Enter weight (T) to reverse-calc qty, or enter qty to auto-fill weight"
                : undefined
          }
          onChange={(e) => {
            lastEditRef.current = "weight";
            const v = e.target.value === "" ? 0 : Number(e.target.value);
            form.setValue(`lines.${index}.weight`, v, { shouldDirty: true });
            if (rules?.autoTubeWeight && v > 0) {
              const q = qtyFromTotalWeight(buildTubeDims(), v);
              if (q != null) {
                form.setValue(`lines.${index}.qty`, q, { shouldDirty: true });
              }
            }
          }}
        />
        {wtPerMm != null ? (
          <div className="px-0.5 text-[9px] tabular-nums text-muted-foreground">{wtPerMm} wt/mm</div>
        ) : null}
      </LineGridTd>
      <LineGridTd>
        <input
          type="number"
          step="0.01"
          inputMode="decimal"
          className={cellControlClass("fs-no-spin text-right tabular-nums")}
          {...form.register(`lines.${index}.unit_rate`)}
          disabled={!linesEditable}
          onWheel={blockNumberWheel}
          onKeyDown={onRowKeyDown}
          title="₹ per ton (weight)"
        />
      </LineGridTd>
      <LineGridTd>
        <select
          className={cellControlClass("min-w-[4.5rem] font-medium tabular-nums")}
          value={form.watch(`lines.${index}.gst_slab`)}
          disabled={!linesEditable}
          onKeyDown={onRowKeyDown}
          title={
            gstMode === "intra_state"
              ? "GST slab — splits into CGST + SGST"
              : "GST slab — applied as IGST"
          }
          onChange={(e) => {
            form.setValue(`lines.${index}.gst_slab`, Number(e.target.value), {
              shouldDirty: true,
            });
          }}
        >
          {slabs.map((s) => {
            const half = s / 2;
            const detail =
              s === 0
                ? "Exempt"
                : gstMode === "intra_state"
                  ? `${half}+${half}`
                  : `IGST`;
            return (
              <option key={s} value={s}>
                {s === 0 ? "0% Exempt" : `${s}% (${detail})`}
              </option>
            );
          })}
        </select>
      </LineGridTd>
      <LineGridTd>
        <input
          className={cellControlClass("text-[10px]")}
          {...form.register(`lines.${index}.description`)}
          disabled={!linesEditable}
          placeholder="Auto spec…"
          onKeyDown={onRowKeyDown}
          title={form.watch(`lines.${index}.description`) || undefined}
        />
      </LineGridTd>
      <LineGridTd align="right" className="px-1">
        <div className="tabular-nums text-xs font-medium">{formatMoney(lineAmount)}</div>
        <div className="text-[10px] leading-tight text-muted-foreground">{taxLabel}</div>
      </LineGridTd>
      <LineGridTd className="w-7 text-center">
        {canRemove ? (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className="size-6 hover:text-destructive"
            onClick={onRemove}
            aria-label={`Remove line ${index + 1}`}
          >
            <Trash2 className="size-3" />
          </Button>
        ) : null}
      </LineGridTd>
    </LineGridRow>
  );
}
